import { useState } from "react";
import "./GovernanceProposals.css";

export type ProposalStatus = "open" | "closed";
export type VoteOption = "yes" | "no" | "abstain";

export interface Proposal {
  id: string;
  title: string;
  summary: string;
  status: ProposalStatus;
  endTime: number;
  yesPct: number;
  noPct: number;
  abstainPct: number;
  quorumPct?: number;
  userVote?: VoteOption | null;
}

// Mock data for UI; replace with API when /token/v1/governance/proposal is available
const MOCK_PROPOSALS: Proposal[] = [
  {
    id: "prop-001",
    title: "Add BTC/USDC spot pool",
    summary: "Create a new spot market pair BTC/USDC with 0.05% fee tier and ReClamm support.",
    status: "open",
    endTime: Date.now() + 2 * 24 * 60 * 60 * 1000,
    yesPct: 62,
    noPct: 28,
    abstainPct: 10,
    quorumPct: 45,
    userVote: null,
  },
  {
    id: "prop-002",
    title: "Update protocol fee tier for stable pools",
    summary: "Change default fee from 0.01% to 0.02% for stablecoin pairs.",
    status: "open",
    endTime: Date.now() + 5 * 24 * 60 * 60 * 1000,
    yesPct: 45,
    noPct: 40,
    abstainPct: 15,
    quorumPct: 40,
    userVote: "yes",
  },
  {
    id: "prop-003",
    title: "Upgrade Oracle module to v2",
    summary: "Governance proposal to upgrade the oracle module with new price feeds.",
    status: "open",
    endTime: Date.now() + 1 * 24 * 60 * 60 * 1000,
    yesPct: 78,
    noPct: 12,
    abstainPct: 10,
    userVote: null,
  },
  {
    id: "prop-004",
    title: "Enable HyperClamm hooks for volatility fee",
    summary: "Enable volatility fee hook across all HyperClamm pools.",
    status: "closed",
    endTime: Date.now() - 3 * 24 * 60 * 60 * 1000,
    yesPct: 82,
    noPct: 10,
    abstainPct: 8,
    userVote: "yes",
  },
];

type TabId = "open" | "needs-vote" | "voted" | "closed";

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return "Ended";
  const d = Math.floor(ms / (24 * 60 * 60 * 1000));
  const h = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  if (d > 0) return `Ends in ${d}d`;
  if (h > 0) return `Ends in ${h}h`;
  return "Ending soon";
}

function filterProposals(
  proposals: Proposal[],
  tab: TabId,
  hasWallet: boolean
): Proposal[] {
  switch (tab) {
    case "open":
      return proposals.filter((p) => p.status === "open");
    case "needs-vote":
      return proposals.filter((p) => p.status === "open" && p.userVote == null);
    case "voted":
      return proposals.filter((p) => p.userVote != null);
    case "closed":
      return proposals.filter((p) => p.status === "closed");
    default:
      return proposals;
  }
}

interface GovernanceProposalsProps {
  /** Whether a wallet is connected (for "Needs my vote" and voting). */
  hasWallet?: boolean;
}

export function GovernanceProposals({ hasWallet = false }: GovernanceProposalsProps) {
  const [tab, setTab] = useState<TabId>("open");
  const [proposals] = useState<Proposal[]>(MOCK_PROPOSALS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingVote, setPendingVote] = useState<{ id: string; option: VoteOption } | null>(null);

  const filtered = filterProposals(proposals, tab, hasWallet);
  const selected = proposals.find((p) => p.id === selectedId);

  const tabs: { id: TabId; label: string }[] = [
    { id: "open", label: "Open" },
    { id: "needs-vote", label: "Needs my vote" },
    { id: "voted", label: "Voted" },
    { id: "closed", label: "Closed" },
  ];

  const handleVote = (proposalId: string, option: VoteOption) => {
    setPendingVote({ id: proposalId, option });
  };

  const confirmVote = () => {
    if (!pendingVote) return;
    // TODO: call API POST /token/v1/governance/vote
    // For now just close; in real app update proposal.userVote and refetch
    setPendingVote(null);
    setSelectedId(null);
  };

  return (
    <div className="governance-proposals">
      <div className="governance-tabs">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`governance-tab ${tab === id ? "active" : ""}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="governance-content">
        {filtered.length === 0 ? (
          <p className="governance-empty">
            {tab === "open" && "No open proposals right now. Check back later or view closed proposals."}
            {tab === "needs-vote" && "No proposals need your vote. Connect your wallet to see proposals you haven’t voted on."}
            {tab === "voted" && "You haven’t voted on any proposals yet."}
            {tab === "closed" && "No closed proposals to show."}
          </p>
        ) : (
          <>
            <div className="governance-table-wrap">
              <table className="governance-table">
                <thead>
                  <tr>
                    <th>Proposal</th>
                    <th>Status</th>
                    <th>Ends</th>
                    <th>Result</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const timeLeft = formatTimeLeft(p.endTime - Date.now());
                    const result =
                      p.status === "closed"
                        ? `Yes ${p.yesPct}% · No ${p.noPct}%`
                        : `Yes ${p.yesPct}% · No ${p.noPct}%`;
                    return (
                      <tr key={p.id}>
                        <td>
                          <button
                            type="button"
                            className="governance-proposal-title"
                            onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                          >
                            {p.title}
                          </button>
                        </td>
                        <td>
                          <span className={`governance-status governance-status--${p.status}`}>
                            {p.status === "open" ? "Open" : "Closed"}
                          </span>
                          {p.userVote != null && p.status === "open" && (
                            <span className="governance-badge">You voted</span>
                          )}
                        </td>
                        <td className="governance-ends">{timeLeft}</td>
                        <td className="governance-result">{result}</td>
                        <td>
                          {p.status === "open" &&
                            (p.userVote != null ? (
                              <span className="governance-voted-label">
                                Voted {p.userVote === "yes" ? "Yes" : p.userVote === "no" ? "No" : "Abstain"}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="governance-vote-btn"
                                onClick={() => setSelectedId(p.id)}
                              >
                                Vote
                              </button>
                            ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Card layout for small screens */}
            <div className="governance-cards">
              {filtered.map((p) => {
                const timeLeft = formatTimeLeft(p.endTime - Date.now());
                const result = `Yes ${p.yesPct}% · No ${p.noPct}%`;
                return (
                  <div key={p.id} className="governance-card">
                    <button
                      type="button"
                      className="governance-card-title"
                      onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                    >
                      {p.title}
                    </button>
                    <div className="governance-card-meta">
                      <span className={`governance-status governance-status--${p.status}`}>
                        {p.status === "open" ? "Open" : "Closed"}
                      </span>
                      <span className="governance-ends">{timeLeft}</span>
                    </div>
                    <p className="governance-card-result">{result}</p>
                    {p.status === "open" &&
                      (p.userVote != null ? (
                        <span className="governance-voted-label">
                          You voted {p.userVote === "yes" ? "Yes" : p.userVote === "no" ? "No" : "Abstain"}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="governance-vote-btn"
                          onClick={() => setSelectedId(p.id)}
                        >
                          Vote
                        </button>
                      ))}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Proposal detail (expand / modal-like) */}
        {selected && (
          <div className="governance-detail">
            <div className="governance-detail-card">
              <div className="governance-detail-header">
                <h2>{selected.title}</h2>
                <button
                  type="button"
                  className="governance-detail-close"
                  onClick={() => {
                    setSelectedId(null);
                    setPendingVote(null);
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p className="governance-detail-summary">{selected.summary}</p>
              <dl className="governance-detail-meta">
                <dt>Status</dt>
                <dd>
                  <span className={`governance-status governance-status--${selected.status}`}>
                    {selected.status === "open" ? "Open" : "Closed"}
                  </span>
                </dd>
                <dt>Ends</dt>
                <dd>{formatTimeLeft(selected.endTime - Date.now())}</dd>
                <dt>Result</dt>
                <dd>Yes {selected.yesPct}% · No {selected.noPct}% · Abstain {selected.abstainPct}%</dd>
                {selected.quorumPct != null && (
                  <>
                    <dt>Quorum</dt>
                    <dd>{selected.quorumPct}%</dd>
                  </>
                )}
              </dl>
              {selected.status === "open" && !selected.userVote && (
                <div className="governance-vote-actions">
                  {pendingVote?.id === selected.id ? (
                    <div className="governance-confirm">
                      <p>
                        You are voting <strong>{pendingVote.option === "yes" ? "Yes" : pendingVote.option === "no" ? "No" : "Abstain"}</strong> on
                        &quot;{selected.title}&quot;. Submit?
                      </p>
                      <div className="governance-confirm-btns">
                        <button type="button" className="governance-vote-btn primary" onClick={confirmVote}>
                          Submit vote
                        </button>
                        <button
                          type="button"
                          className="governance-vote-btn secondary"
                          onClick={() => setPendingVote(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="governance-vote-option yes"
                        onClick={() => handleVote(selected.id, "yes")}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className="governance-vote-option no"
                        onClick={() => handleVote(selected.id, "no")}
                      >
                        No
                      </button>
                      <button
                        type="button"
                        className="governance-vote-option abstain"
                        onClick={() => handleVote(selected.id, "abstain")}
                      >
                        Abstain
                      </button>
                    </>
                  )}
                </div>
              )}
              {selected.status === "open" && selected.userVote != null && (
                <p className="governance-detail-voted">
                  You voted <strong>{selected.userVote === "yes" ? "Yes" : selected.userVote === "no" ? "No" : "Abstain"}</strong>.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
